/**
 * tracking.ts
 *
 * Player data processing + Canvas renderer.
 *
 * Player input format:
 *   {
 *     id:    string | number,
 *     name?: string,
 *     color?: string,           // CSS color; auto-assigned if omitted
 *     data:  Array<{ timestamp: number, x: number, y: number }>
 *   }
 *
 * GPS convention (when coordType = "gps"):  x = longitude,  y = latitude
 */

import {
  canonicalToCanvas,
  computeViewport,
  localToCanonical,
} from './ground';
import type {
  CanvasPoint,
  GroundContext,
  PlayerInput,
  ProcessedPlayer,
  ProcessedSample,
  RenderOptions,
  Viewport,
  CoordType,
  PlaybackOptions,
  PlaybackController, PlayerTrackInput, ProcessedTrackPlayer, ProcessedTrackSample, TrackSample,
} from './types';

// ─── CONSTANTS ────────────────────────────────────────────────────────────────

const AUTO_COLORS: readonly string[] = [
  '#e63946', '#2196f3', '#4caf50', '#ff9800', '#9c27b0',
  '#00bcd4', '#ff5722', '#8bc34a', '#3f51b5', '#f06292',
  '#ffd600', '#00897b', '#6d4c41', '#546e7a', '#ec407a',
  '#26a69a', '#7e57c2', '#d4e157', '#ef5350', '#42a5f5',
];

export const DEFAULT_RENDER_OPTIONS: RenderOptions = {
  padding: 24,
  pitchColor: '#2d7a1f',
  lineColor: '#ffffff',
  lineWidth: 1.5,
  playerRadius: 10,
  playerFontSize: 9,
  showName: true,
  showTrail: true,
  trailLength: 40,
  trailAlpha: 0.5,
  cornerLabels: true,
  cornerFontSize: 11,
};

// ─── PLAYER PROCESSING ───────────────────────────────────────────────────────

/**
 * Process a single player's tracking series into canvas-ready samples.
 *
 * @param player     Raw player data.
 * @param ctx        Ground context from `buildGroundContext()`.
 * @param coordType  `"local"` or `"gps"` — global setting, same for all players.
 * @param colorIdx   Auto-color index (managed by `processAllPlayers`).
 */
export function processPlayerSeries(
  player: PlayerInput,
  ctx: GroundContext,
  coordType: CoordType = 'local',
  colorIdx = 0,
): ProcessedPlayer {
  const { data } = player;
  if (!Array.isArray(data) || data.length === 0) {
    throw new Error(
      `Player "${player.id}": data must be a non-empty array of { timestamp, x, y }.`,
    );
  }

  const isGps = coordType === 'gps';
  if (isGps && !ctx.gpsProjector) {
    throw new Error(
      "coordType 'gps' requires a gpsAnchor when calling buildGroundContext().",
    );
  }

  const project = ctx.gpsProjector!;

  const samples: ProcessedSample[] = data.map((s) => {
    // GPS: x = lon, y = lat  →  project(lat, lon)
    const local = isGps
      ? project(s.x, s.y)
      : { x: s.x, y: s.y };
    const uv = localToCanonical(local, ctx);
    return {
      t: s.timestamp, x: local.x, y: local.y, u: uv.u, v: uv.v,
    };
  });

  return {
    id: player.id,
    name: player.name ?? String(player.id),
    color: player.color ?? AUTO_COLORS[colorIdx % AUTO_COLORS.length],
    samples,
    current: samples[0] ?? null,
    currentIndex: 0,
    trailSamples: [],
  };
}

/**
 * Process multiple players at once.
 *
 * @param players    Array of raw player inputs.
 * @param ctx        Ground context.
 * @param coordType  `"local"` or `"gps"` — applies to all players.
 */
export function processAllPlayers(
  players: PlayerInput[],
  ctx: GroundContext,
  coordType: CoordType = 'local',
): ProcessedPlayer[] {
  return players.map((p, i) => processPlayerSeries(p, ctx, coordType, i));
}

// ─── TIME SEEK ────────────────────────────────────────────────────────────────

/**
 * Binary-search `samples` for the index closest to timestamp `t`.
 */
export function nearestSampleIndex(samples: ProcessedSample[], t: number): number {
  let lo = 0;
  let hi = samples.length - 1;
  while (lo < hi) {
    // eslint-disable-next-line no-bitwise
    const mid = (lo + hi) >> 1;
    if (samples[mid].t < t) lo = mid + 1;
    else hi = mid;
  }
  if (lo > 0 && Math.abs(samples[lo - 1].t - t) < Math.abs(samples[lo].t - t)) {
    lo -= 1;
  }
  return lo;
}

/**
 * Mutate every player's `.current`, `.currentIndex`, and `.trailSamples`
 * to match timestamp `t`.
 *
 * @param players     Processed player array (mutated in place).
 * @param t           Target timestamp.
 * @param trailLength How many past samples to expose in `.trailSamples`.
 */
export function seekToTime(
  players: ProcessedPlayer[],
  t: number,
  trailLength = 0,
): void {
  // eslint-disable-next-line no-restricted-syntax
  for (const p of players) {
    const idx = nearestSampleIndex(p.samples, t);
    p.current = p.samples[idx];
    p.currentIndex = idx;
    p.trailSamples = trailLength > 0
      ? p.samples.slice(Math.max(0, idx - trailLength), idx + 1)
      : [];
  }
}

// ─── CANVAS RENDERER ─────────────────────────────────────────────────────────

/**
 * Draw all pitch markings (boundary, centre line, circles, penalty areas, goals).
 */
export function drawPitch(
  ctx2d: CanvasRenderingContext2D,
  vp: Viewport,
  opts: RenderOptions,
): void {
  const {
    x, y, w, h,
  } = vp;
  ctx2d.save();

  ctx2d.fillStyle = opts.pitchColor;
  ctx2d.fillRect(x, y, w, h);

  ctx2d.strokeStyle = opts.lineColor;
  ctx2d.lineWidth = opts.lineWidth;
  ctx2d.setLineDash([]);

  ctx2d.strokeRect(x, y, w, h);

  // Centre line
  ctx2d.beginPath();
  ctx2d.moveTo(x + w / 2, y);
  ctx2d.lineTo(x + w / 2, y + h);
  ctx2d.stroke();

  // Centre circle (FIFA r = 9.15 m on 105 × 68 m pitch)
  const rC = ((9.15 / 105) * w + (9.15 / 68) * h) / 2;
  ctx2d.beginPath();
  ctx2d.arc(x + w / 2, y + h / 2, rC, 0, 2 * Math.PI);
  ctx2d.stroke();

  // Centre spot
  ctx2d.fillStyle = opts.lineColor;
  ctx2d.beginPath();
  ctx2d.arc(x + w / 2, y + h / 2, 2, 0, 2 * Math.PI);
  ctx2d.fill();

  // Penalty areas
  const paW = (16.5 / 105) * w;
  const paH = (40.32 / 68) * h;
  const paY = y + (h - paH) / 2;
  ctx2d.strokeRect(x, paY, paW, paH);
  ctx2d.strokeRect(x + w - paW, paY, paW, paH);

  // Goal areas
  const gaW = (5.5 / 105) * w;
  const gaH = (18.32 / 68) * h;
  const gaY = y + (h - gaH) / 2;
  ctx2d.strokeRect(x, gaY, gaW, gaH);
  ctx2d.strokeRect(x + w - gaW, gaY, gaW, gaH);

  // Penalty spots
  const psX = (11 / 105) * w;
  ctx2d.beginPath();
  ctx2d.arc(x + psX, y + h / 2, 2, 0, 2 * Math.PI);
  ctx2d.arc(x + w - psX, y + h / 2, 2, 0, 2 * Math.PI);
  ctx2d.fill();

  // Goals
  const goalH = (7.32 / 68) * h;
  const goalD = (2.44 / 105) * w;
  const goalY = y + (h - goalH) / 2;
  ctx2d.strokeRect(x - goalD, goalY, goalD, goalH);
  ctx2d.strokeRect(x + w, goalY, goalD, goalH);

  ctx2d.restore();
}

/**
 * Draw all players (trail + dot + label) at their current `.current` sample.
 */
export function drawPlayers(
  ctx2d: CanvasRenderingContext2D,
  players: ProcessedPlayer[],
  vp: Viewport,
  opts: RenderOptions,
): void {
  for (const player of players) {
    if (!player.current) continue;
    const { cx, cy }: CanvasPoint = canonicalToCanvas(player.current, vp);

    // Trail
    if (opts.showTrail && player.trailSamples.length > 1) {
      ctx2d.save();
      for (let i = 1; i < player.trailSamples.length; i++) {
        const alpha = (i / player.trailSamples.length) * opts.trailAlpha;
        const p0 = canonicalToCanvas(player.trailSamples[i - 1], vp);
        const p1 = canonicalToCanvas(player.trailSamples[i], vp);
        ctx2d.strokeStyle = player.color;
        ctx2d.globalAlpha = alpha;
        ctx2d.lineWidth = 2;
        ctx2d.beginPath();
        ctx2d.moveTo(p0.cx, p0.cy);
        ctx2d.lineTo(p1.cx, p1.cy);
        ctx2d.stroke();
      }
      ctx2d.restore();
    }

    // Dot
    ctx2d.save();
    ctx2d.beginPath();
    ctx2d.arc(cx, cy, opts.playerRadius, 0, 2 * Math.PI);
    ctx2d.fillStyle = player.color;
    ctx2d.fill();
    ctx2d.strokeStyle = '#fff';
    ctx2d.lineWidth = 1.5;
    ctx2d.stroke();

    // Label
    if (opts.showName) {
      ctx2d.fillStyle = '#fff';
      ctx2d.font = `bold ${opts.playerFontSize}px sans-serif`;
      ctx2d.textAlign = 'center';
      ctx2d.textBaseline = 'middle';
      ctx2d.fillText(String(player.name).substring(0, 3), cx, cy);
    }
    ctx2d.restore();
  }
}

/**
 * Draw A / B / C / D corner labels.
 */
export function drawCornerLabels(
  ctx2d: CanvasRenderingContext2D,
  vp: Viewport,
  opts: RenderOptions,
): void {
  const corners = [
    { label: 'A', u: 0, v: 0 },
    { label: 'B', u: 0, v: 1 },
    { label: 'C', u: 1, v: 1 },
    { label: 'D', u: 1, v: 0 },
  ] as const;

  ctx2d.save();
  ctx2d.font = `bold ${opts.cornerFontSize}px monospace`;
  ctx2d.fillStyle = 'rgba(255,255,255,0.85)';
  ctx2d.textBaseline = 'middle';

  for (const c of corners) {
    const { cx, cy } = canonicalToCanvas(c, vp);
    ctx2d.fillText(c.label, cx + (c.u === 0 ? -14 : 6), cy + (c.v === 0 ? 10 : -10));
  }
  ctx2d.restore();
}

/**
 * Full render: seek all players to `t`, then draw pitch + players on `canvas`.
 */
export function renderFrame(
  canvas: HTMLCanvasElement,
  groundCtx: GroundContext,
  players: ProcessedPlayer[],
  t: number,
  userOpts: Partial<RenderOptions> = {},
): void {
  const opts: RenderOptions = { ...DEFAULT_RENDER_OPTIONS, ...userOpts };
  const ctx2d = canvas.getContext('2d');
  if (!ctx2d) return;

  seekToTime(players, t, opts.showTrail ? opts.trailLength : 0);

  const vp = computeViewport(canvas.width, canvas.height, groundCtx, opts.padding);

  ctx2d.clearRect(0, 0, canvas.width, canvas.height);
  ctx2d.fillStyle = '#1a1a2e';
  ctx2d.fillRect(0, 0, canvas.width, canvas.height);

  drawPitch(ctx2d, vp, opts);
  drawPlayers(ctx2d, players, vp, opts);
  if (opts.cornerLabels) drawCornerLabels(ctx2d, vp, opts);
}

// ─── PLAYBACK CONTROLLER ─────────────────────────────────────────────────────

/**
 * Create a `requestAnimationFrame`-based playback loop.
 * Returns a controller with `start`, `stop`, and `seek`.
 *
 * The controller starts playing immediately.
 */
export function createPlayback(
  canvas: HTMLCanvasElement,
  groundCtx: GroundContext,
  players: ProcessedPlayer[],
  opts: PlaybackOptions = {},
): PlaybackController {
  const speed = opts.speed ?? 1;
  const loop = opts.loop ?? true;
  const renderOpts = opts.renderOptions ?? {};

  const allT = players.flatMap((p) => p.samples.map((s) => s.t));
  const tMin = Math.min(...allT);
  const tMax = Math.max(...allT);

  let tCurrent = tMin;
  let lastWall: number | null = null;
  let rafId: number | null = null;
  let running = false;

  function frame(wallTime: number): void {
    if (lastWall !== null) tCurrent += (wallTime - lastWall) * speed;
    lastWall = wallTime;

    if (tCurrent > tMax) {
      if (loop) tCurrent = tMin;
      else { stop(); return; }
    }

    renderFrame(canvas, groundCtx, players, tCurrent, renderOpts);
    rafId = requestAnimationFrame(frame);
  }

  function start(): void {
    if (running) return;
    running = true;
    lastWall = null;
    rafId = requestAnimationFrame(frame);
  }

  function stop(): void {
    running = false;
    if (rafId !== null) cancelAnimationFrame(rafId);
    rafId = null;
  }

  function seek(t: number): void {
    tCurrent = t;
    renderFrame(canvas, groundCtx, players, t, renderOpts);
  }

  start();

  return {
    start,
    stop,
    seek,
    get tMin() { return tMin; },
    get tMax() { return tMax; },
  };
}

// ─── TRACK FORMAT PROCESSING ─────────────────────────────────────────────────

/**
 * Process a single player whose data arrives in the "Track" format:
 * a plain object keyed by timestamp, where each value is a sample record
 * containing x, y plus optional telemetry (speed, heading, cardio, mask).
 *
 * Samples where BOTH x and y are null are skipped — these represent moments
 * when the player is off-field or the GPS signal was lost.
 * Samples where only one of x/y is null are also skipped (incomplete position).
 *
 * The returned ProcessedTrackPlayer is a drop-in replacement for ProcessedPlayer
 * and is compatible with seekToTime(), renderFrame(), and all rendering helpers.
 * The extra telemetry fields (speed, heading, cardio, mask) are available on
 * each sample and on .current for use in custom renderers or tooltips.
 *
 * @param player     Raw player with TrackData.
 * @param ctx        Ground context from buildGroundContext().
 * @param vp         Viewport
 * @param coordType  "local" | "gps" — global setting, same for all players.
 * @param colorIdx   Auto-color palette index (managed by processAllTracks).
 */
export function processPlayerTrack(
  player: PlayerTrackInput,
  ctx: GroundContext,
  vp: Viewport,
  coordType: CoordType = 'local',
  colorIdx = 0,
): ProcessedTrackPlayer {
  const { data } = player;

  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    throw new Error(
      `Player "${player.id}": data must be a Record<timestamp, TrackSample>.`,
    );
  }

  const isGps = coordType === 'gps';
  if (isGps && !ctx.gpsProjector) {
    throw new Error(
      "coordType 'gps' requires a gpsAnchor when calling buildGroundContext().",
    );
  }

  const project = ctx.gpsProjector!;

  // Object.entries preserves insertion order for string keys; for numeric keys
  // JS engines sort them ascending numerically — which matches timestamp order.
  // const samples: ProcessedTrackSample[] = [];
  const samples: {
    [t: string]: Omit<ProcessedTrackSample, 't'>
  } = {};

  for (const [key, s] of Object.entries(data)) {
    // Skip samples with missing position
    if (s.x === null || s.y === null) continue;

    const t = Number(key);
    if (!Number.isFinite(t)) continue; // guard against malformed keys

    const local = isGps
      ? project(s.x, s.y)
      : { x: s.x, y: s.y };

    const uv = localToCanonical(local, ctx);
    const canvasPoint = canonicalToCanvas(uv, vp);

    samples[t] = {
      x: local.x,
      y: local.y,
      u: uv.u,
      v: uv.v,
      cx: canvasPoint.cx,
      cy: canvasPoint.cy,
      cardio: s.cardio,
      heading: s.heading,
      mask: s.mask,
      speed: s.speed,
    };
  }

  /* if (samples.length === 0) {
    throw new Error(
      `Player "${player.id}": no valid samples found (all x/y pairs are null).`,
    );
  } */

  // Guarantee chronological order regardless of key insertion order
  // samples.sort((a, b) => a.t - b.t);

  return {
    id: player.id,
    name: player.name ?? String(player.id),
    color: player.color ?? AUTO_COLORS[colorIdx % AUTO_COLORS.length],
    samples,
    current: null,
    currentIndex: 0,
    trailSamples: [],
  };
}

/**
 * Process a single TrackSample at a known timestamp into a ProcessedTrackSample.
 *
 * Use this when you already have one isolated point — e.g. the latest live
 * position from a streaming feed — and don't need to build a full
 * ProcessedTrackPlayer.
 *
 * Returns null when x or y is null (position unavailable).
 *
 * @param timestamp  The sample's timestamp (same unit as the rest of the track).
 * @param sample     The raw TrackSample object.
 * @param ctx        Ground context from buildGroundContext().
 * @param coordType  "local" | "gps" — must match the rest of the track.
 */
export function processPlayerTrackPoint(
  sample: {x: number | null, y: number | null},
  ctx: GroundContext,
  vp: Viewport,
  coordType: CoordType = 'local',
): Omit<ProcessedTrackSample, 't'> | null {
  if (sample.x === null || sample.y === null) return null;

  const isGps = coordType === 'gps';
  if (isGps && !ctx.gpsProjector) {
    throw new Error(
      "coordType 'gps' requires a gpsAnchor when calling buildGroundContext().",
    );
  }

  const local = isGps
    ? ctx.gpsProjector!(sample.x, sample.y)
    : { x: sample.x, y: sample.y };

  const uv = localToCanonical(local, ctx);
  const canvasPoint = canonicalToCanvas(uv, vp);

  return {
    x:       local.x,
    y:       local.y,
    u:       uv.u,
    v:       uv.v,
    cx:      canvasPoint.cx,
    cy:      canvasPoint.cy,
  };
}



/**
 * Process multiple players in Track format at once.
 *
 * @param players    Array of PlayerTrackInput objects.
 * @param ctx        Ground context.
 * @param coordType  "local" | "gps" — applies to all players.
 */
export function processAllTracks(
  players: PlayerTrackInput[],
  ctx: GroundContext,
  coordType: CoordType = 'local',
): ProcessedTrackPlayer[] {
  return players.map((p, i) => processPlayerTrack(p, ctx, coordType, i));
}
